import { faFileInvoice } from '@fortawesome/pro-regular-svg-icons'
import {
  Checkbox,
  Flex,
  Select,
  Text,
  Textarea,
} from '@mantine/core'
import { useMemo } from 'react'

import { useTranslation } from '@flecto/translations'
import { CollapsibleCard } from '@flecto/ui'

import { CheckboxWithDescription } from './CheckboxWithDescription'
import { useFormProfileBookingsContext } from './FormProfileBookings'

export function SectionProforma({
  termsAndConditionsEmpty,
}: {
  termsAndConditionsEmpty: boolean
}) {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_bookings' })

  const form = useFormProfileBookingsContext()
  const DUE_DATE_DAYS_DEFAULT = 30
  const checkboxItems = useMemo(
    () => [
      {
        label: t('label_qr_code'),
        description: t('label_qr_code_description'),
        value: form.values.documents_proforma_include_links,
        field: 'documents_proforma_include_links',
      },
      {
        label: t('label_include_terms'),
        description: t('label_include_terms_description'),
        value: form.values.documents_proforma_include_terms_and_conditions,
        field: 'documents_proforma_include_terms_and_conditions',
      },
    ],
    [form.values, t]
  )

  return (
    <CollapsibleCard opened>
      <CollapsibleCard.Title>{t('documents_proforma')}</CollapsibleCard.Title>
      <CollapsibleCard.Description>
        {t('documents_proforma_description')}
      </CollapsibleCard.Description>
      <CollapsibleCard.Icon withCircleBackground icon={faFileInvoice} />
      <CollapsibleCard.Content>
        <Flex direction='column' gap='lg'>
          <Flex direction='column'>
            <Checkbox
              label={t('label_due_date')}
              onChange={(e) =>
                form.setFieldValue(
                  'documents_proforma_include_due_date',
                  e.target.checked
                )
              }
              checked={form.values.documents_proforma_include_due_date}
            />

            <Text c='dimmed' size='sm' ml='lg' pl='xs'>
              {t('label_due_date_description')}
            </Text>

            {form.values.documents_proforma_include_due_date && (
              <>
                <Text ml='lg' pl='xs' mb='sm' mt='md'>
                  {t('label_due_date_in')}
                </Text>

                <Select
                  placeholder={`30 ${t('label_due_date_days')}`}
                  maw='150px'
                  ml='lg'
                  pl='xs'
                  value={(
                    form.values.documents_proforma_due_date_in_days ||
                    DUE_DATE_DAYS_DEFAULT
                  ).toString()}
                  onChange={(value: string | null) =>
                    form.setFieldValue(
                      'documents_proforma_due_date_in_days',
                      Number(value)
                    )
                  }
                  data={[
                    { value: '30', label: `30 ${t('label_due_date_days')}` },
                    { value: '60', label: `60 ${t('label_due_date_days')}` },
                    { value: '90', label: `90 ${t('label_due_date_days')}` },
                  ]}
                  error={form.errors.documents_proforma_include_due_date}
                />
              </>
            )}
          </Flex>

          <Flex direction='column'>
            <Checkbox
              label={t('label_payment_details')}
              onChange={(e) =>
                form.setFieldValue(
                  'documents_proforma_include_payment_details',
                  e.target.checked
                )
              }
              checked={form.values.documents_proforma_include_payment_details}
            />

            <Text c='dimmed' size='sm' ml='lg' pl='xs'>
              {t('label_payment_details_description')}
            </Text>

            {form.values.documents_proforma_include_payment_details && (
              <>
                <Text ml='lg' pl='xs' mb='sm' mt='md'>
                  {t('label_field_payment_details')}
                </Text>

                <Textarea
                  ml='lg'
                  pl='xs'
                  value={form.values.documents_proforma_payment_details}
                  onChange={(event) =>
                    form.setFieldValue(
                      'documents_proforma_payment_details',
                      event.currentTarget.value
                    )
                  }
                  autosize
                  minRows={3}
                />
              </>
            )}
          </Flex>

          {checkboxItems.map((item, index) => (
            <CheckboxWithDescription
              key={index}
              label={item.label}
              description={item.description}
              value={item.value}
              onChange={(value: boolean) =>
                form.setFieldValue(item.field, value)
              }
              disabled={
                item.field ===
                  'documents_proforma_include_terms_and_conditions' &&
                termsAndConditionsEmpty
              }
            />
          ))}
        </Flex>
      </CollapsibleCard.Content>
    </CollapsibleCard>
  )
}
